import { useEffect, useState, useCallback } from "react";
import { Mic, Volume2, Loader2, Save, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api";
import { voiceApi } from "@/lib/voice";
import { useAuth } from "@/context/AuthContext";
import { tenantService } from "@/services";
import { Switch } from "@/components/ui/switch";

const VOICE_HINTS = {
  alloy: "Neutral", echo: "Calm male", fable: "Expressive", onyx: "Deep male",
  nova: "Warm female", shimmer: "Bright female", coral: "Friendly", sage: "Soft", ash: "Confident",
};

export default function VoiceConfigPage() {
  const { user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const load = useCallback(async () => {
    try { setCfg(await voiceApi.getConfig(tenantId ? { tenantId } : {})); }
    catch { setCfg(null); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await voiceApi.saveConfig({ enabled: cfg.enabled, voiceId: cfg.voiceId, enabledLangs: cfg.enabledLangs || [], tenantId: tenantId || undefined });
      toast.success("Voice configuration saved");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const testVoice = async () => {
    setTesting(true);
    try {
      const url = await voiceApi.speak("Hello, this is your ConnectIQ voice assistant. How can I help you today?", cfg.voiceId);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail) || "Could not play sample"); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>;
  const needTenant = isSuper && !tenantId;

  return (
    <div data-testid="voice-config-page" className="max-w-2xl">
      <header className="flex items-center gap-3 mb-6">
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Mic className="h-5 w-5" /></span>
        <div>
          <h1 className="text-xl font-bold text-foreground">Voice AI Configuration</h1>
          <p className="text-sm text-muted-foreground">Whisper speech-to-text · OpenAI text-to-speech</p>
        </div>
      </header>

      {isSuper && (
        <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="mb-5 bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="voice-tenant-select">
          <option value="">Select tenant…</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
        </select>
      )}

      <div className="bg-card border border-border rounded-xl p-5 space-y-5">
        <div className="flex items-center justify-between" data-testid="voice-availability">
          <div className="flex items-center gap-2 text-sm">
            {cfg.available ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
            <span className="text-foreground">Voice engine {cfg.available ? "operational" : "unavailable"}</span>
          </div>
          <span className="text-xs text-muted-foreground">whisper-1 · gpt-4o-mini-tts</span>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-4">
          <div>
            <div className="text-sm font-medium text-foreground">Enable voice for this tenant</div>
            <div className="text-xs text-muted-foreground">Mic-to-ask & read-aloud in the Copilot</div>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} disabled={needTenant} data-testid="voice-enable-switch" />
        </div>

        <div className="border-t border-border pt-4">
          <div className="text-sm font-medium text-foreground mb-2">Default assistant voice</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="voice-options">
            {(cfg.voices || []).map((v) => (
              <button key={v} onClick={() => setCfg({ ...cfg, voiceId: v })}
                className={`text-left rounded-lg border px-3 py-2 transition-colors ${cfg.voiceId === v ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                data-testid={`voice-option-${v}`}>
                <div className="text-sm font-medium capitalize text-foreground">{v}</div>
                <div className="text-[11px] text-muted-foreground">{VOICE_HINTS[v] || ""}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-border pt-4">
          <button onClick={testVoice} disabled={testing || !cfg.available} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm disabled:opacity-50" data-testid="test-voice-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} Test "{cfg.voiceId}"
          </button>
          <button onClick={save} disabled={saving || needTenant} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 text-sm disabled:opacity-50 ml-auto" data-testid="save-voice-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
